import { inject, Injectable } from '@angular/core';
import {
  CheckoutTabsDisabled,
  CheckoutTab,
  SaveAddressDto,
  UserAddress,
  CheckoutTotals,
  ProcessOrderResponse,
  ProcessStationDto,
} from '../models';
import { NgxAimService } from '../ngx-aim.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressType, CheckoutTabNames, CheckoutType, GAEventAction } from '../enums';
import { TaxAmountDto, TotalBeforeTaxDto } from '../models/dto/tax.dto';
import { HttpErrorService } from './http-error.service';
import { PaymentService } from './payment.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { GenericCheckoutService } from './generic-checkout.service';
import { StationsService } from './stations.service';
import { AppStateService } from './app-state.service';

@Injectable({
  providedIn: 'root'
})
export class StationsCheckoutService implements GenericCheckoutService {
  ngxAimService = inject(NgxAimService);
  appState = inject(AppStateService);
  http = inject(HttpClient);
  httpErrorService = inject(HttpErrorService);
  paymentService = inject(PaymentService);
  gaService = inject(GoogleAnalyticsService);
  stationsService = inject(StationsService);

  private baseUrl = this.ngxAimService.getApiUrl();

  public tabs: CheckoutTab[] = [];

  private tabIndexSubject = new BehaviorSubject<number>(0);
  public tabIndex$ = this.tabIndexSubject.asObservable();
  public setTabIndex(index: number) {
    this.tabIndexSubject.next(index);
  }
  public setTabIndexByHeader(header: CheckoutTabNames) {
    const tab = this.tabs.find((tab) => tab.header === header);
    if (tab) {
      this.tabIndexSubject.next(tab.index);
    } else {
      console.error(`unable to find tab header: ${header}`);
    }
  }

  private tabsDisabledSubject = new BehaviorSubject<CheckoutTabsDisabled>({
    [CheckoutTabNames.BILLING_ADDRESS]: false,
    [CheckoutTabNames.PAYMENT]: true,
    [CheckoutTabNames.OVERVIEW]: true,
  });
  public tabsDisabed$ = this.tabsDisabledSubject.asObservable();
  public setTabIsDisabled(tabIndex: CheckoutTabNames, isDisabled: boolean) {
    const tabs = this.tabsDisabledSubject.value;
    tabs[tabIndex] = isDisabled;
    this.tabsDisabledSubject.next(tabs);
  }

  private isLoadingTotalsSubject = new BehaviorSubject<boolean>(false);
  public isLoadingTotals$ = this.isLoadingTotalsSubject.asObservable();
  private totalsSubject = new BehaviorSubject<CheckoutTotals>({
    items: 0,
    shipping: 0,
    tax: 0,
    total: 0,
  });
  public totals$ = this.totalsSubject.asObservable();

  async calculateTotals() {
    this.isLoadingTotalsSubject.next(true);
    const itemTotal = this.getStationTotal() + this.getAddOnsTotal();
    const tax = await this.getTaxAmount({
      goodsAndServicesAmount: itemTotal,
      shippingAmount: 0,
    });

    this.totalsSubject.next({
      items: itemTotal,
      shipping: 0,
      tax: tax,
      total: itemTotal + tax,
    });
    this.isLoadingTotalsSubject.next(false);
  }

  public updateTotals() {
    this.calculateTotals();
  }

  getStationTotal() {
    const selectedRate = this.stationsService.getSelectedRate()!;
    const selectedGroup = this.stationsService.getSelectedGroup()!;
    const durationByInterval = this.stationsService.getSelectedDuration()! / selectedGroup.Interval;
    return selectedRate.Rate * (selectedGroup.Interval / selectedRate.Unit) * durationByInterval;
  }

  getAddOnsTotal() {
    return this.stationsService.getSelectedAddOns()!.reduce((acc, item) => {
      return acc + (item.cartQty * item.Price);
    }, 0);
  }

  private userBillingAddressSubject = new BehaviorSubject<Partial<SaveAddressDto>>({});
  public userBillingAddress$ = this.userBillingAddressSubject.asObservable();
  public getUserBillingAddress() {
    return this.userBillingAddressSubject.value;
  }

  saveAddress(address: SaveAddressDto): Observable<void> {
    this.userBillingAddressSubject.next(address);
    return this.http.post<void>(`${this.baseUrl}/users/save-address`, address);
  }

  getAddress(type: AddressType): Observable<UserAddress> {
    return this.http.get<UserAddress>(`${this.baseUrl}/users/get-address`, {
      params: new HttpParams()
        .set('type', type)
    });
  }

  getTaxAmount(totalBeforeTax: TotalBeforeTaxDto): Promise<number> {
    return new Promise<number>((resolve) => {
      this.http.post<TaxAmountDto>(`${this.baseUrl}/payment/tax`, totalBeforeTax).subscribe({
        next: (res) => resolve(res.amount),
        error: (err) => {
          if (this.appState.isBrowser) {
            this.httpErrorService.onHttpError(err, 'Could not calculate sales tax');
          }
          resolve(0);
        }
      });
    });
  }

  checkout(): Observable<ProcessOrderResponse> {
    const dto: ProcessStationDto = {
      LocationId: this.stationsService.getSelectedLocation()!.Id,
      GroupId: this.stationsService.getSelectedGroup()!.Id,
      StationId: this.stationsService.getSelectedStation()!.Id,
      Start: this.stationsService.getSelectedTime()!.time,
      Length: this.stationsService.getSelectedDuration()!,
      Guests: this.stationsService.getSelectedAmountOfGuests(),
      Notes: this.stationsService.Notes,
      Items: this.stationsService.getSelectedAddOns(),
      StationRateId: this.stationsService.getSelectedRate()!.Id,
      needsRangeSafetyOfficer: this.stationsService.getSelectedNeedsRSO(),
      cardData: this.paymentService.cardData,
    }
    return this.http.post<ProcessOrderResponse>(`${this.baseUrl}/stations/checkout`, dto);
  }

  emitGaAddPaymentInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_PAYMENT_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.total,
      checkout_type: CheckoutType.STATION,
      items: [{
        item_id: this.stationsService.getSelectedStation()?.Id,
        item_name: this.stationsService.getSelectedStation()?.WebDescription,
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  emitGaAddShippingInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_SHIPPING_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.total,
      checkout_type: CheckoutType.STATION,
      items: [{
        item_id: this.stationsService.getSelectedStation()?.Id,
        item_name: this.stationsService.getSelectedStation()?.WebDescription,
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  public reset() {
    this.paymentService.reset();
    this.tabIndexSubject.next(0);
    this.tabsDisabledSubject.next({
      [CheckoutTabNames.BILLING_ADDRESS]: true,
      [CheckoutTabNames.PAYMENT]: true,
      [CheckoutTabNames.OVERVIEW]: true,
    });

    this.totalsSubject.next({
      items: 0,
      shipping: 0,
      tax: 0,
      total: 0,
    });
  }
}

